import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Download, Trash2, Search, X, Move, Archive, Pencil } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import DocumentPreview, { PreviewButton } from "../components/DocumentPreview";
import FolderTree, { FolderBreadcrumb } from "../components/FolderTree";
import ClientUploadZone from "../components/ClientUploadZone";
import { fileSizeLabel, formatDate } from "../lib/constants";
import { sortDocuments, selectionAfterClick, SortHeader, fileExt, useElapsed } from "../components/DocTableUtils";
import { useAuth } from "../lib/auth";

export default function ClientDocuments() {
  const { project } = useOutletContext();
  const { user } = useAuth();
  const isClientAdmin = user?.role === "client" && (user?.permission === "edit" || user?.client_role === "admin");
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [currentFolder, setCurrentFolder] = useState("__all__");
  const [previewing, setPreviewing] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [showMove, setShowMove] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [renameModal, setRenameModal] = useState(null);
  const [sortKey, setSortKey] = useState("title");
  const [sortDir, setSortDir] = useState("asc");
  const [folderDownloadId, setFolderDownloadId] = useState(null);
  const [downloadingBulk, setDownloadingBulk] = useState(false);
  const anchorRef = useRef(null);
  const folderElapsed = useElapsed(!!folderDownloadId);
  const allElapsed = useElapsed(downloading);
  const bulkElapsed = useElapsed(downloadingBulk);

  const loadAll = useCallback(async () => {
    const [{ data: fs }, { data: ds }] = await Promise.all([
      api.get(`/projects/${project.id}/folders`),
      api.get(`/projects/${project.id}/documents`),
    ]);
    setFolders(fs);
    setDocuments(ds);
  }, [project.id]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { setSelectedIds(new Set()); setSearch(""); }, [currentFolder]);

  const folderById = useMemo(() => Object.fromEntries(folders.map((f) => [f.id, f])), [folders]);

  const folderFiltered = useMemo(() => {
    if (currentFolder === "__all__") return documents;
    if (currentFolder === "__root__") return documents.filter((d) => !d.folder_id);
    return documents.filter((d) => d.folder_id === currentFolder);
  }, [documents, currentFolder]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? folderFiltered.filter(
          (d) =>
            (d.title || "").toLowerCase().includes(q) ||
            (d.file_name || "").toLowerCase().includes(q) ||
            (d.drawing_number || "").toLowerCase().includes(q) ||
            (d.description || "").toLowerCase().includes(q),
        )
      : folderFiltered;
    return sortDocuments(list, sortKey, sortDir, {
      folder: (d) => (d.folder_id ? folderById[d.folder_id]?.name || "" : ""),
    });
  }, [folderFiltered, search, sortKey, sortDir, folderById]);

  const selectableIds = filtered.map((d) => d.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.has(id));

  const onSort = (key) => {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };
  const handleRowClick = (id, e) => {
    const { selected, anchor } = selectionAfterClick(selectedIds, anchorRef.current, id, e, selectableIds);
    setSelectedIds(selected);
    anchorRef.current = anchor;
  };

  const toggleSelect = (id) => setSelectedIds((prev) => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next;
  });
  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => { const n = new Set(prev); selectableIds.forEach((id) => n.delete(id)); return n; });
    } else {
      setSelectedIds((prev) => { const n = new Set(prev); selectableIds.forEach((id) => n.add(id)); return n; });
    }
  };

  const download = async (doc) => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = doc.file_name || "file"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const downloadSelected = async () => {
    if (selectedIds.size === 0 || busy) return;
    setBusy(true); setDownloadingBulk(true);
    try {
      const resp = await api.post(
        `/projects/${project.id}/documents/bulk-download`,
        { document_ids: Array.from(selectedIds) },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = "documents.zip"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); setDownloadingBulk(false); }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0 || busy) return;
    if (!confirm(`Delete ${selectedIds.size} file(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/documents/bulk-delete`, { document_ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      await loadAll();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const downloadAll = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await api.get(`/projects/${project.id}/download-all`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${project.project_reference || "project"}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setDownloading(false); }
  };

  const downloadFolder = async (folder) => {
    if (folderDownloadId) return;
    setFolderDownloadId(folder.id);
    try {
      const resp = await api.get(`/projects/${project.id}/folders/${folder.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a"); a.href = url; a.download = `${folder.name || "folder"}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setFolderDownloadId(null); }
  };

  const moveSelected = async (targetFolderId) => {
    if (selectedIds.size === 0 || busy) return;
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/documents/move`, {
        document_ids: Array.from(selectedIds),
        folder_id: targetFolderId || null,
      });
      setSelectedIds(new Set());
      setShowMove(false);
      await loadAll();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const renameFolder = async (fid, newName) => {
    try {
      await api.patch(`/projects/${project.id}/folders/${fid}`, { name: newName });
      await loadAll();
    } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const headerLabel = currentFolder === "__all__"
    ? "All documents"
    : currentFolder === "__root__"
      ? "Unfiled"
      : folderById[currentFolder]?.name || "Folder";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8" data-testid="client-documents">
      <aside>
        <div className="label mb-4">Folders</div>
        <FolderTree
          folders={folders}
          documents={documents}
          currentFolder={currentFolder}
          onSelect={setCurrentFolder}
          onRename={isClientAdmin ? (folder) => setRenameModal({ id: folder.id, name: folder.name }) : null}
          onDownload={downloadFolder}
          downloadingFolderId={folderDownloadId}
          downloadElapsed={folderElapsed}
          testIdPrefix="client-folder"
        />
        {/* Download all ZIP */}
        <button
          onClick={downloadAll}
          disabled={downloading}
          className="mt-4 w-full flex items-center gap-2 justify-center text-xs text-[color:var(--atech-text-dim)] hover:text-[color:var(--atech-copper)] border border-[color:var(--atech-border)] rounded-[2px] py-2 transition-colors"
          data-testid="download-all-zip"
        >
          <Archive size={13} />
          {downloading ? `Preparing… ${allElapsed}s` : "Download all as ZIP"}
        </button>
      </aside>

      <div>
        <FolderBreadcrumb folders={folders} currentFolder={currentFolder} onSelect={setCurrentFolder} />

        {/* Search + Toolbar */}
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="label shrink-0">
              {headerLabel}
              {selectedIds.size > 0 && (
                <span className="ml-2 text-xs font-normal text-[color:var(--atech-copper)]">· {selectedIds.size} selected</span>
              )}
            </div>
            <div className="relative flex-1 max-w-xs">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] pointer-events-none" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name, number…"
                className="w-full pl-7 pr-7 py-1.5 bg-transparent border border-[color:var(--atech-border)] rounded-[2px] text-xs text-[color:var(--atech-text)] placeholder:text-[color:var(--atech-text-muted)] focus:outline-none focus:border-[color:var(--atech-copper)]"
                data-testid="client-docs-search"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[color:var(--atech-text-muted)] hover:text-white">
                  <X size={11} />
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowMove(true)}
              disabled={busy || selectedIds.size === 0}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-[2px] border transition-colors ${
                selectedIds.size > 0
                  ? "border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-white hover:border-[color:var(--atech-border-strong)]"
                  : "border-[color:var(--atech-border)]/40 text-[color:var(--atech-text-muted)] opacity-40 cursor-not-allowed"
              }`}
              data-testid="client-move-docs-button"
              title={selectedIds.size === 0 ? "Select files to move" : `Move ${selectedIds.size} file(s)`}
            >
              <Move size={12} /> Move{selectedIds.size > 0 ? ` ${selectedIds.size}` : ""}
            </button>
            <button
              onClick={downloadSelected}
              disabled={busy || selectedIds.size === 0}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-[2px] border transition-colors ${
                selectedIds.size > 0
                  ? "border-[color:var(--atech-border)] text-[color:var(--atech-text-dim)] hover:text-white hover:border-[color:var(--atech-border-strong)]"
                  : "border-[color:var(--atech-border)]/40 text-[color:var(--atech-text-muted)] opacity-40 cursor-not-allowed"
              }`}
              data-testid="client-bulk-download-button"
              title={selectedIds.size === 0 ? "Select files to download" : `Download ${selectedIds.size} file(s)`}
            >
              <Download size={12} />
              {downloadingBulk ? `Preparing… ${bulkElapsed}s` : selectedIds.size > 0 ? `Download ${selectedIds.size}` : "Download"}
            </button>
            <button
              onClick={deleteSelected}
              disabled={busy || selectedIds.size === 0}
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-[2px] border transition-colors ${
                selectedIds.size > 0
                  ? "border-[#F87171]/40 text-[#F87171] hover:bg-[#F87171]/10"
                  : "border-[color:var(--atech-border)]/40 text-[color:var(--atech-text-muted)] opacity-40 cursor-not-allowed"
              }`}
              data-testid="client-bulk-delete-button"
              title={selectedIds.size === 0 ? "Select files to delete" : `Delete ${selectedIds.size} file(s)`}
            >
              <Trash2 size={12} /> Delete{selectedIds.size > 0 ? ` ${selectedIds.size}` : ""}
            </button>
          </div>
        </div>

        {/* Document list — explorer style */}
        <div className="card-surface overflow-hidden p-0">
          {filtered.length === 0 ? (
            <div className="p-12 text-center">
              {search ? (
                <>
                  <div className="text-sm text-[color:var(--atech-text)]">No results for &ldquo;{search}&rdquo;</div>
                  <button onClick={() => setSearch("")} className="text-xs text-[color:var(--atech-copper)] mt-2">Clear search</button>
                </>
              ) : (
                <>
                  <div className="text-sm text-[color:var(--atech-text)]">Nothing here yet.</div>
                  <p className="text-xs text-[color:var(--atech-text-dim)] mt-1.5 max-w-md mx-auto leading-relaxed">
                    No documents have been shared in this folder yet. A-Tech will upload drawings, reports, and correspondence here as your project progresses.
                  </p>
                </>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="client-docs-table">
                <thead>
                  <tr className="border-b border-[color:var(--atech-border)]">
                    <th className="w-10 px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        className="accent-[color:var(--atech-copper)]"
                        data-testid="client-select-all-docs"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                    <SortHeader label="Name" colKey="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortHeader label="Type" colKey="type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortHeader label="Size" colKey="file_size" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    <SortHeader label="Date modified" colKey="created_at" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    {currentFolder === "__all__" && (
                      <SortHeader label="Folder" colKey="folder" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                    )}
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <ClientDocumentRow
                      key={d.id}
                      doc={d}
                      folderName={d.folder_id ? folderById[d.folder_id]?.name : null}
                      showFolderName={currentFolder === "__all__"}
                      selected={selectedIds.has(d.id)}
                      onToggle={() => toggleSelect(d.id)}
                      onRowClick={(e) => handleRowClick(d.id, e)}
                      onPreview={() => setPreviewing(d)}
                      onDownload={() => download(d)}
                    />
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2 text-[11px] text-[color:var(--atech-text-muted)] border-t border-[color:var(--atech-border)]/50">
                Tip: click a column heading to sort. Use Ctrl/Cmd or Shift + click to select several files, then Download.
              </div>
            </div>
          )}
        </div>

        {project?.client_uploads_enabled && (
          <ClientUploadZone projectId={project.id} folders={folders} onUploaded={loadAll} />
        )}
      </div>

      {previewing && <DocumentPreview doc={previewing} onClose={() => setPreviewing(null)} />}

      {renameModal && (
        <RenameFolderModal
          folder={renameModal}
          onClose={() => setRenameModal(null)}
          onSave={async (newName) => { await renameFolder(renameModal.id, newName); setRenameModal(null); }}
        />
      )}

      {showMove && (
        <MoveFolderModal
          folders={folders}
          onMove={moveSelected}
          onClose={() => setShowMove(false)}
          busy={busy}
        />
      )}
    </div>
  );
}

function ClientDocumentRow({ doc, folderName, showFolderName, selected, onToggle, onRowClick, onPreview, onDownload }) {
  const ext = fileExt(doc.file_name);
  return (
    <tr
      className={`border-b border-white/5 cursor-default transition-colors ${selected ? "bg-[rgba(198,132,88,0.10)]" : "hover:bg-white/[0.02]"}`}
      data-testid={`client-doc-${doc.id}`}
      onClick={onRowClick}
      onDoubleClick={onPreview}
    >
      <td className="px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="accent-[color:var(--atech-copper)]"
          data-testid={`client-doc-select-${doc.id}`}
        />
      </td>
      <td className="px-4 py-3 align-middle max-w-[320px]">
        <div className="text-sm text-[color:var(--atech-text)] truncate">{doc.title}</div>
        <div className="text-[11px] text-[color:var(--atech-text-muted)] font-mono truncate">{doc.file_name}</div>
      </td>
      <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{ext || "—"}</td>
      <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{fileSizeLabel(doc.file_size)}</td>
      <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{formatDate(doc.created_at)}</td>
      {showFolderName && (
        <td className="px-4 py-3 align-middle text-xs text-[color:var(--atech-text-dim)] whitespace-nowrap">{folderName || "—"}</td>
      )}
      <td className="px-4 py-3 align-middle" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1 justify-end">
          <PreviewButton onClick={onPreview} testId={`client-preview-${doc.id}`} />
          {doc.storage_path ? (
            <button
              onClick={onDownload}
              title="Download"
              className="p-2 text-[color:var(--atech-text-dim)] hover:text-white hover:bg-white/5 rounded-[2px]"
              data-testid={`client-download-${doc.id}`}
            >
              <Download size={14} />
            </button>
          ) : (
            <span className="text-[10px] text-[color:var(--atech-text-muted)] uppercase tracking-[0.16em] px-2">Placeholder</span>
          )}
        </div>
      </td>
    </tr>
  );
}

function MoveFolderModal({ folders, onMove, onClose, busy }) {
  const rootFolders = folders.filter((f) => !f.parent_id);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="move-folder-modal">
      <div className="card-surface w-full max-w-sm p-6 space-y-4">
        <div className="label">Move selected files to…</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          <button
            onClick={() => onMove(null)}
            disabled={busy}
            className="w-full text-left px-3 py-2 text-sm rounded-[2px] hover:bg-white/5 text-[color:var(--atech-text-dim)]"
            data-testid="move-to-root"
          >
            / Root (unfiled)
          </button>
          {folders.map((f) => (
            <button
              key={f.id}
              onClick={() => onMove(f.id)}
              disabled={busy}
              className="w-full text-left px-3 py-2 text-sm rounded-[2px] hover:bg-white/5 text-[color:var(--atech-text)]"
              style={{ paddingLeft: `${(f.depth || 0) * 16 + 12}px` }}
              data-testid={`move-to-folder-${f.id}`}
            >
              {f.name}
            </button>
          ))}
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
        </div>
      </div>
    </div>
  );
}


function RenameFolderModal({ folder, onClose, onSave }) {
  const [name, setName] = useState(folder.name);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try { await onSave(trimmed); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="client-rename-folder-modal">
      <div className="card-surface w-full max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="label">Rename folder</div>
        <form onSubmit={submit} className="space-y-4">
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-[color:var(--atech-surface-elevated)] border border-[color:var(--atech-border)] rounded-[2px] px-3 py-2.5 text-sm text-[color:var(--atech-text)] focus:outline-none focus:border-[color:var(--atech-copper)]"
            data-testid="client-rename-folder-input"
          />
          <div className="flex justify-end gap-3">
            <button type="button" onClick={onClose} className="text-sm text-[color:var(--atech-text-dim)]">Cancel</button>
            <button type="submit" disabled={busy || !name.trim()} className="btn-copper text-sm" data-testid="client-rename-folder-save">
              {busy ? "Saving…" : "Rename"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
